// Selector propio, igual en Windows, macOS, iOS y Android (el <select> nativo se dibuja distinto en cada
// sistema). Patrón WAI-ARIA «combobox de solo selección»: el foco se queda en el botón y la opción activa
// se anuncia con aria-activedescendant. En pantallas estrechas la lista se abre como hoja inferior.
import { type KeyboardEvent, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { IconTick } from './icons';
import styles from './Select.module.css';

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

const TYPEAHEAD_MS = 600;
const LIST_MAX_PX = 288;

export function Select<T extends string>({
  label, hideLabel = false, value, options, onChange, variant = 'field', placeholder, active = false, disabled = false,
}: {
  label: string;
  hideLabel?: boolean;
  value: T;
  options: Array<SelectOption<T>>;
  onChange: (value: T) => void;
  variant?: 'field' | 'pill' | 'ghost';
  placeholder?: string;
  /** Pastilla resaltada: el filtro está filtrando algo. */
  active?: boolean;
  disabled?: boolean;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [upward, setUpward] = useState(false);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = options[selectedIndex];
  const optionId = (index: number) => `${id}-opcion-${index}`;

  function openList() {
    if (disabled || options.length === 0) return;
    const rect = trigger.current?.getBoundingClientRect();
    // Hacia arriba si no cabe debajo (p. ej. la plantilla, junto al editor).
    setUpward(!!rect && window.innerHeight - rect.bottom < LIST_MAX_PX + 16 && rect.top > window.innerHeight - rect.bottom);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }

  function close(focusTrigger = true) {
    setOpen(false);
    if (focusTrigger) trigger.current?.focus();
  }

  function choose(index: number) {
    const option = options[index];
    if (option) onChange(option.value);
    close();
  }

  // Cerrar al pulsar fuera.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  // La opción activa siempre a la vista.
  useLayoutEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' });
  });

  function typeahead(char: string) {
    const now = Date.now();
    const text = now - typed.current.at < TYPEAHEAD_MS ? typed.current.text + char : char;
    typed.current = { text, at: now };
    const needle = text.toLocaleLowerCase('es');
    const start = open ? activeIndex + (text.length === 1 ? 1 : 0) : selectedIndex + 1;
    for (let step = 0; step < options.length; step += 1) {
      const index = (start + step) % options.length;
      if (options[index]!.label.toLocaleLowerCase('es').startsWith(needle)) {
        if (open) setActiveIndex(index);
        else onChange(options[index]!.value);
        return;
      }
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const last = options.length - 1;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        openList();
      } else if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        typeahead(event.key);
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); setActiveIndex((index) => Math.min(last, index + 1)); break;
      case 'ArrowUp': event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 1)); break;
      case 'Home': event.preventDefault(); setActiveIndex(0); break;
      case 'End': event.preventDefault(); setActiveIndex(last); break;
      case 'PageDown': event.preventDefault(); setActiveIndex((index) => Math.min(last, index + 8)); break;
      case 'PageUp': event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 8)); break;
      case 'Enter':
      case ' ': event.preventDefault(); choose(activeIndex); break;
      case 'Escape': event.preventDefault(); close(); break;
      case 'Tab': close(false); break;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) typeahead(event.key);
    }
  }

  return (
    <div className={`${styles.root} ${styles[variant]}`} ref={root} data-active={active || undefined}>
      <label id={`${id}-label`} htmlFor={`${id}-trigger`} className={hideLabel ? 'visually-hidden' : styles.label}>{label}</label>
      <button ref={trigger} id={`${id}-trigger`} type="button" role="combobox" className={styles.trigger}
        aria-haspopup="listbox" aria-expanded={open} aria-controls={`${id}-lista`}
        aria-labelledby={`${id}-label ${id}-trigger`}
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        disabled={disabled} onClick={() => (open ? close() : openList())} onKeyDown={onKeyDown}>
        <span className={selected ? styles.value : styles.placeholder}>{selected?.label ?? placeholder ?? ''}</span>
        <svg className={styles.chevron} viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m7 10 5 5 5-5" /></svg>
      </button>
      {open && (
        <>
          <div className={styles.backdrop} aria-hidden="true" onPointerDown={() => close(false)} />
          <div className={styles.popover} data-upward={upward || undefined}>
            <p className={styles.sheetTitle} aria-hidden="true">{label}</p>
            <ul ref={list} id={`${id}-lista`} role="listbox" aria-labelledby={`${id}-label`} className={styles.list} tabIndex={-1}>
              {options.map((option, index) => (
                <li key={option.value} id={optionId(index)} role="option" aria-selected={option.value === value}
                  className={styles.option} data-active={index === activeIndex || undefined}
                  onPointerMove={() => setActiveIndex(index)}
                  onClick={() => choose(index)}>
                  <span className={styles.optionLabel}>{option.label}</span>
                  {option.value === value && <IconTick className={styles.check} />}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
